import { ImportButton, useRuleEditorValue, useSetRuleEditor } from '@innovatm/js-rule-editor';
import { Button } from '@mui/material';
import { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { GetGetRulesResponse, IRuleGeneric, KeyGetGetRules, useMutationPostCreateRule } from '../../backend';
import { useCurrentUserClient } from '../../contexts/OidcContext/OidcContext.selectors';
import { ROUTE_EDITOR } from '../../routes';
import { errorToastConfig, successToastConfig } from '../../utils/utils';
import {
    Header,
    LeftComponentsHeader,
    MiddleComponentsHeader,
    RightComponentsHeader,
} from '../navigation/Header/Header.style';
import { Nav } from '../navigation/Nav/Nav';
import { PageTitle } from '../navigation/Nav/PageTitle';
import { CustomSearch } from '../navigation/Search/CustomSearch';

export const RuleEditorPageHeader = memo(function RuleEditorPageHeader() {
    const client = useCurrentUserClient();
    const navigate = useNavigate();

    const goToType = useCallback(() => {
        navigate(`${ROUTE_EDITOR}/ResourceManagerRule`);
    }, [navigate]);

    const queryClient = useQueryClient();
    const { mutate: postCreateRule } = useMutationPostCreateRule({
        onSuccess: ({ data: rule }) => {
            navigate(ROUTE_EDITOR);
            queryClient.setQueriesData(
                { queryKey: [KeyGetGetRules, { context: client.airportIata }] },
                (values: GetGetRulesResponse): GetGetRulesResponse => ({
                    ...values,
                    data: values.data.concat([rule]),
                }),
            );
            toast(`The rule ${rule.name} is successfully created.`, successToastConfig);
        },

        onError: error => {
            Object.keys(error?.response?.data).map(errorKey =>
                toast(`${error?.response?.data[errorKey]}`, errorToastConfig),
            );
        },
    });
    const createRule = useCallback(
        (nonEditableRuleRule: IRuleGeneric) => {
            postCreateRule({
                nonEditableRuleResourceManagerRuleRule: nonEditableRuleRule,
                context: client.airportIata,
            });
        },
        [client.airportIata, postCreateRule],
    );

    return (
        <Header>
            <LeftComponentsHeader>
                <Nav>
                    <PageTitle>
                        <FormattedMessage id="nav.title.ruleeditor" />
                    </PageTitle>
                </Nav>
            </LeftComponentsHeader>
            <MiddleComponentsHeader>
                <CustomSearch search={useRuleEditorValue('searchText')} setSearch={useSetRuleEditor('searchText')} />
            </MiddleComponentsHeader>
            <RightComponentsHeader>
                <ImportButton onImport={createRule} sx={{ color: 'white' }} />
                &emsp;
                <Button
                    variant="outlined"
                    onClick={goToType}
                    sx={{ color: 'white', borderColor: 'white', borderRadius: '7px' }}
                >
                    New Rule
                </Button>
            </RightComponentsHeader>
        </Header>
    );
});
